import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link, useNavigate } from "react-router-dom";
import { META, generateRichSnippet } from "./meta";
import { ForgotPasswordFormData, ForgotPasswordFormState } from "./types";
import { submitForgotPasswordForm } from "./forms.actions";
import { useAuth } from "@/core/components/Auth/AuthContext";
import { BUSINESS_NAME } from "@/core/constants/business";

const ForgotPasswordPage: React.FC = () => {
  const richSnippet = generateRichSnippet();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState<ForgotPasswordFormData>({
    email: "",
  });
  const [formState, setFormState] = useState<ForgotPasswordFormState>({
    isSubmitting: false,
    isSuccess: false,
    isError: false,
  });
  const [errors, setErrors] = useState<{ email?: string }>({});

  // Redirect if already logged in
  useEffect(() => {
    if (isAuthenticated) {
      navigate("/account");
    }
  }, [isAuthenticated, navigate]);

  const validateForm = (): boolean => {
    const newErrors: { email?: string } = {};

    if (!formData.email.trim()) {
      newErrors.email = "Email address is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error when field changes
    if (errors[name as keyof typeof errors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Reset form state
    setFormState({
      isSubmitting: false,
      isSuccess: false,
      isError: false,
    });

    if (!validateForm()) {
      return;
    }

    setFormState((prev) => ({ ...prev, isSubmitting: true }));

    try {
      const response = await submitForgotPasswordForm(formData);

      if (response.success) {
        setFormState({
          isSubmitting: false,
          isSuccess: true,
          isError: false,
        });
      } else {
        setFormState({
          isSubmitting: false,
          isSuccess: false,
          isError: true,
          errorMessage: response.message,
        });
      }
    } catch (error) {
      setFormState({
        isSubmitting: false,
        isSuccess: false,
        isError: true,
        errorMessage: "An unexpected error occurred. Please try again later.",
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>{META.title}</title>
        <meta name="description" content={META.meta_description} />
        <meta name="keywords" content={META.keywords} />
        <link
          rel="canonical"
          href={`https://slsdispensary.com${META.canonicalUrl}`}
        />

        {/* OpenGraph Meta Tags */}
        <meta property="og:title" content={META.title} />
        <meta property="og:description" content={META.meta_description} />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content={`https://slsdispensary.com${META.canonicalUrl}`}
        />
        <meta property="og:site_name" content={BUSINESS_NAME} />

        {/* JSON-LD for rich snippets */}
        <script type="application/ld+json">
          {JSON.stringify(richSnippet)}
        </script>
      </Helmet>

      <section className="relative py-20 bg-gradient-to-b from-law-black to-gray-900 text-white">
        <div className="absolute inset-0 z-0 opacity-20">
          <picture>
            <source
              srcSet="/images/street-lawyer-services-dc-dispensary-entrance.webp"
              type="image/webp"
            />
            <img
              src="/images/street-lawyer-services-dc-dispensary-entrance.jpg"
              alt="Street Lawyer Services Dispensary Background"
              className="w-full h-full object-cover"
            />
          </picture>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-md mx-auto">
            <div className="text-center mb-8">
              <h1 className="text-4xl font-bold mb-2">Reset Your Password</h1>
              <p className="text-gray-300">
                Enter your email address and we'll send you instructions to
                reset your password
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-xl p-8 text-gray-900">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Success Message */}
                {formState.isSuccess && (
                  <div className="p-4 mb-6 text-sm border-l-4 border-green-500 bg-green-50 text-green-700">
                    <div className="font-medium">Check Your Email</div>
                    <p>
                      We've sent password reset instructions to your email
                      address. Please check your inbox (and spam folder) for the
                      reset link.
                    </p>
                  </div>
                )}

                {/* Error Message */}
                {formState.isError && (
                  <div className="p-4 mb-6 text-sm border-l-4 border-red-500 bg-red-50 text-red-700">
                    <div className="font-medium">Password Reset Failed</div>
                    <p>{formState.errorMessage || "Please try again later."}</p>
                  </div>
                )}

                {/* Email Field */}
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-cannabis-green-300 focus:border-cannabis-green-500 transition-colors ${
                      errors.email ? "border-red-500" : "border-gray-300"
                    }`}
                    placeholder="your.email@example.com"
                    disabled={formState.isSubmitting || formState.isSuccess}
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                  )}
                </div>

                {/* Submit Button */}
                <div>
                  <button
                    type="submit"
                    className="w-full px-8 py-3 bg-cannabis-green-500 hover:bg-cannabis-green-600 text-white font-medium rounded-lg transition duration-300 flex items-center justify-center"
                    disabled={formState.isSubmitting || formState.isSuccess}
                  >
                    {formState.isSubmitting ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Processing...
                      </>
                    ) : formState.isSuccess ? (
                      <>
                        <svg
                          className="w-5 h-5 mr-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M5 13l4 4L19 7"
                          ></path>
                        </svg>
                        Email Sent
                      </>
                    ) : (
                      "Reset Password"
                    )}
                  </button>
                </div>
              </form>

              {/* Navigation Links */}
              <div className="mt-8 pt-6 border-t border-gray-200 text-center">
                <div className="flex flex-col space-y-2">
                  <Link
                    to="/login"
                    className="text-cannabis-green-600 hover:text-cannabis-green-500 text-sm hover:underline"
                  >
                    Return to sign in
                  </Link>
                  <Link
                    to="/signup"
                    className="text-cannabis-green-600 hover:text-cannabis-green-500 text-sm hover:underline"
                  >
                    Create new account
                  </Link>
                </div>
              </div>
            </div>

            <div className="text-center mt-8">
              <p className="text-sm text-gray-400">
                Need assistance? Contact us at{" "}
                <a
                  href="mailto:support@streetlawyerservices.com"
                  className="text-cannabis-green-400 hover:text-cannabis-green-300"
                >
                  support@streetlawyerservices.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ForgotPasswordPage;
